import React, { createContext, useContext, useMemo } from 'react';
import type { AssetLicenseEntry } from '../../types/isomorphic/license.types';
import type { AssetDownloadAgreementsResponse } from '../../types/isomorphic/download.types';

interface ModifiedDownloadContextValue {
  genId: string | null;
  index: number | null;
  onModifiedDownloadSuccess: () => void;
  hasTransparency: boolean;
  assetLicense: AssetLicenseEntry | null;
  downloadAgreements: AssetDownloadAgreementsResponse | null;
  hideALaCarte: boolean;
}

interface ModifiedDownloadProviderProps {
  children: React.ReactNode;
  genId?: string | null;
  index?: number | null;
  hasTransparency?: boolean;
  onModifiedDownloadSuccess?: () => void;
  assetLicense?: AssetLicenseEntry | null;
  downloadAgreements?: AssetDownloadAgreementsResponse | null;
  hideALaCarte?: boolean;
}

const ModifiedDownloadContext =
  createContext<ModifiedDownloadContextValue | null>(null);

const useModifiedDownloadContext = () => {
  const context = useContext(ModifiedDownloadContext);
  if (!context) {
    throw new Error(
      'useModifiedDownloadContext must be used within a ModifiedDownloadProvider'
    );
  }
  return context;
};

const ModifiedDownloadProvider = ({
  children,
  genId = null,
  index = null,
  hasTransparency = false,
  onModifiedDownloadSuccess = () => {},
  assetLicense = null,
  downloadAgreements = null,
  hideALaCarte = false,
}: ModifiedDownloadProviderProps) => (
  <ModifiedDownloadContext.Provider
    value={useMemo(
      () => ({
        genId,
        index,
        onModifiedDownloadSuccess,
        hasTransparency,
        assetLicense,
        downloadAgreements,
        hideALaCarte,
      }),
      [
        genId,
        index,
        onModifiedDownloadSuccess,
        hasTransparency,
        assetLicense,
        downloadAgreements,
        hideALaCarte,
      ]
    )}
  >
    {children}
  </ModifiedDownloadContext.Provider>
);

export {
  ModifiedDownloadContext,
  useModifiedDownloadContext,
  ModifiedDownloadProvider,
};
